/**
 * Filtros de la bandeja → parámetros de `GET /api/v1/alerts`.
 *
 * Separado de la vista porque es la parte que se puede probar sin montar
 * nada, y porque un filtro mal armado no falla: devuelve otra bandeja. Un
 * analista que cree estar viendo "todas las abiertas" y está viendo otra cosa
 * cierra el turno pensando que no hay nada pendiente.
 *
 * Una limitación conocida del cliente, no de la API: `status` y `alert_type`
 * son repetibles en el endpoint (`?status=open&status=investigating`), pero
 * `encodeQuery` de `api/client.ts` arma la query con `URLSearchParams.set`,
 * que deja un solo valor por clave. Mientras eso siga así, acá se elige un
 * estado y un tipo por vez. El resto de los filtros sí se combinan.
 */
import type { QueryParams } from '@/api/types';
import type { AlertStatus } from './severity';

export type Assignment = 'all' | 'mine' | 'unassigned';
export type SortOrder = 'severity' | 'recent';

export interface InboxFilters {
  /** `null` = todos los estados. */
  status: AlertStatus | null;
  /** Severidad mínima, 1..5. `null` = todas. */
  severityMin: number | null;
  /** Nombre de la regla (`impossible_travel`). `null` = todas. */
  alertType: string | null;
  assignment: Assignment;
  /** Ventana hacia atrás en días. `null` = sin límite. */
  sinceDays: number | null;
  sort: SortOrder;
}

/**
 * Lo que ve un investigador al entrar: lo que hay que atender, lo más grave
 * primero. Sin esto la bandeja abre en "lo último que pasó", que es la vista
 * de monitoreo y no la de triage.
 */
export const DEFAULT_FILTERS: InboxFilters = {
  status: 'open',
  severityMin: null,
  alertType: null,
  assignment: 'all',
  sinceDays: 90,
  sort: 'severity',
};

const DAY_MS = 86_400_000;

/**
 * `QueryParams` para el hook de paginación. Las claves que no se filtran no
 * se mandan: `undefined` no viaja (ver `encodeQuery`).
 *
 * También es la clave de cache (`alertKeys.list`), así que tiene que ser un
 * objeto plano y estable: dos filtros distintos son dos entradas de cache.
 */
export function toQueryParams(filters: InboxFilters, now: Date): QueryParams {
  const params: QueryParams = { sort: filters.sort };

  if (filters.status !== null) params['status'] = filters.status;
  if (filters.severityMin !== null) params['severity_min'] = filters.severityMin;
  if (filters.alertType !== null) params['alert_type'] = filters.alertType;
  if (filters.assignment === 'mine') params['mine'] = true;
  if (filters.assignment === 'unassigned') params['unassigned'] = true;
  if (filters.sinceDays !== null) {
    // El corte se calcula acá y no en el servidor para que quede en la clave
    // de cache: si viajara como "últimos 90 días" a secas, dos consultas
    // hechas con horas de diferencia compartirían entrada.
    params['since'] = new Date(now.getTime() - filters.sinceDays * DAY_MS).toISOString();
  }
  return params;
}

/** Cuántos filtros se apartan del default. Va en el botón de "limpiar". */
export function activeFilterCount(filters: InboxFilters): number {
  let total = 0;
  if (filters.status !== DEFAULT_FILTERS.status) total += 1;
  if (filters.severityMin !== DEFAULT_FILTERS.severityMin) total += 1;
  if (filters.alertType !== DEFAULT_FILTERS.alertType) total += 1;
  if (filters.assignment !== DEFAULT_FILTERS.assignment) total += 1;
  if (filters.sinceDays !== DEFAULT_FILTERS.sinceDays) total += 1;
  if (filters.sort !== DEFAULT_FILTERS.sort) total += 1;
  return total;
}

/**
 * Redondea el corte temporal a la hora en curso.
 *
 * Sin esto, cada render produce un `since` distinto —milisegundo a
 * milisegundo—, con lo que cambia la clave de cache y TanStack Query vuelve a
 * pedir la primera página en cada tecla que se aprieta.
 */
export function stableNow(now: Date): Date {
  const rounded = new Date(now);
  rounded.setMinutes(0, 0, 0);
  return rounded;
}
